import { Alert, Button, List, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlayerPlayFilled } from "@tabler/icons-react";
import { processEOY } from "api/statement.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import React, { useState } from "react";
import EoyYearPicker from "./components/AdminYearPeriodPicker";

const AdminEOYPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleProcess = async () => {
    if (!selectedDate) {
      notifications.show({
        title: "No Month Selected",
        message: "Please select a month before running Month End.",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    setProcessing(true);
    try {
      await processEOY(selectedDate);

      notifications.show({
        title: "Year End Complete",
        message: `Year End for ${selectedDate} has been successfully processed.`,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
    } catch (error: any) {
      notifications.show({
        title: "Year End Failed",
        message:
          error?.response?.data?.error ||
          error?.message ||
          "An unexpected error occurred.",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Alert
        color="red"
        title="⚠️ Year End Processing Warning"
        variant="filled"
        radius="md"
        mb="lg"
      >
        <Text fw={500} mb="sm">
          Before running Year End, please ensure you have run the{" "}
          <a href="statement-invoices">Email Statement/Invoices</a> first. Once
          this process begins, it <strong>cannot be reversed</strong>.
        </Text>

        <Text fw={700} mt="md">
          Important Notes
        </Text>

        <List spacing="xs" mt="xs">
          <List.Item>
            <strong>Date selection:</strong> The specific day you choose does
            not matter. Only the last month of selected <strong>year</strong> is
            used. For example, selecting <em>2026</em> will process December
            2026, using the last day of that month (<em>31/12/2026</em>).
          </List.Item>

          <List.Item>
            <strong>Statements and Invoices:</strong> Please confirm you have
            printed all Statements and Invoices before running Month End. These
            documents cannot be regenerated once Month End is complete.
          </List.Item>
        </List>
      </Alert>
      <Stack mt="lg" gap="md">
        <EoyYearPicker value={selectedDate} onChange={setSelectedDate} />
        <Button
          w={300}
          size="lg"
          color="red"
          leftSection={<IconPlayerPlayFilled size={18} />}
          loading={processing}
          onClick={handleProcess}
        >
          Process Year End
        </Button>
      </Stack>
    </>
  );
};
export default AdminEOYPage;
